import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from './Themes';
import { Develope } from './AllSvgs';

import LogoComponent from '../subComponents/LogoComponent';
import SocialIcons from '../subComponents/SocialIcons';
import PowerButton from '../subComponents/PowerButton';
import ParticleComponent from '../subComponents/ParticleComponent';
import BigTitle from '../subComponents/BigTitlte';

// DarkModeToggle component
const ToggleButton = styled.button`
  background-color: ${(props) => props.theme.body};
  color: ${(props) => props.theme.text};
  border: 2px solid ${(props) => props.theme.text};
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0.5rem 1rem;
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 3;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => props.theme.text};
    color: ${(props) => props.theme.body};
  }
`;

const DarkModeToggle = ({ toggleTheme, isDark }) => {
  return (
    <ToggleButton onClick={toggleTheme}>
      {isDark ? 'Light Mode' : 'Dark Mode'}
    </ToggleButton>
  );
};

const Box = styled.div`
  background: ${(props) => props.theme.body};
  width: 100%;
  min-height: 100vh;
  position: relative;
  padding: 2rem;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  padding-top: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    padding-top: 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding-top: 2rem;
    margin-top: 53px;
    padding: 15px;
    width: 350px;
    padding-right: 10px;
  }
`;

const SkillCard = styled.div`
  background-color: ${(props) => props.theme.body};
  border: 2px solid ${(props) => props.theme.text};
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 1024px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: ${(props) => props.theme.text};
  display: flex;
  align-items: center;
  font-weight: 700;

  svg {
    margin-right: 1rem;
    color: ${(props) => props.theme.text};
  }

  @media (max-width: 1024px) {
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  color: ${(props) => props.theme.text};
  line-height: 1.6;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const SkillList = styled.ul`
  list-style-type: none;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    gap: 0.8rem;
  }
`;

const SkillItem = styled.li`
  background-color: ${(props) => props.theme.text};
  color: ${(props) => props.theme.body};
  padding: 0.6rem 1.2rem;
  border-radius: 50px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  font-weight: 500;

  &:hover {
    background-color: ${(props) => props.theme.body};
    color: ${(props) => props.theme.text};
    border: 1px solid ${(props) => props.theme.text};
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.5rem 1rem;
  }
`;

// const ResumeButton = styled.button`
//   background-color: ${(props) => props.theme.text};
//   color: ${(props) => props.theme.body};
//   padding: 1rem 2.5rem;
//   border-radius: 50px;
//   font-size: 1.1rem;
//   font-weight: 600;
//   cursor: pointer;
//   border: none;
//   transition: all 0.3s ease;
//   box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

//   &:hover {
//     background-color: ${(props) => props.theme.body};
//     color: ${(props) => props.theme.text};
//     border: 1px solid ${(props) => props.theme.text};
//     transform: translateY(-3px);
//     box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
//   }

//   @media (max-width: 768px) {
//     padding: 0.8rem 2rem;
//     font-size: 1rem;
//   }

//   @media (max-width: 480px) {
//     padding: 0.7rem 1.8rem;
//     font-size: 0.9rem;
//   }
// `;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: ${(props) => props.theme.text};
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const MySkillsPage = () => {
  const [theme, setTheme] = useState('light');
  const isDarkTheme = theme === 'dark';

  const toggleTheme = () => {
    setTheme(isDarkTheme ? 'light' : 'dark');
  };

  // const handleResumeDownload = () => {
  //   // Replace '/path_to_your_resume.pdf' with the actual path to your resume file
  //   const resumeUrl = '/path_to_your_resume.pdf';

  //   const link = document.createElement('a');
  //   link.href = resumeUrl;
  //   link.setAttribute('download', 'YourName_Resume.pdf');
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  return (
    <ThemeProvider theme={isDarkTheme ? darkTheme : lightTheme}>
      <Box>
        <LogoComponent theme={theme} />
        <SocialIcons theme={theme} />
        <PowerButton />
        <ParticleComponent theme={theme} />
        <DarkModeToggle toggleTheme={toggleTheme} isDark={isDarkTheme} />
        <Container>
          <SkillCard>
            <Title>
              <Develope width={40} height={40} /> Development
            </Title>
            <Description>
              Versatile developer with extensive experience in creating
              FullStack applications and Low Level implementations. Adapts to
              any Stack very easily. Interested in Network Security and
              Cryptography.
            </Description>
            <SectionTitle>Skills</SectionTitle>
            <SkillList>
              {[
                'MERN Stack',
                'GraphQL',
                'Backend Scaling',
                'ThreeJS & WebGL',
                'NextJS',
                'Websockets',
                'webRTC & tRPC',
                'DevOps',
              ].map((skill) => (
                <SkillItem key={skill}>{skill}</SkillItem>
              ))}
            </SkillList>
            <SectionTitle>Tools</SectionTitle>
            <SkillList>
              {['Docker', 'AWS', 'Git', 'API', 'Chromium', 'Gecko'].map(
                (tool) => (
                  <SkillItem key={tool}>{tool}</SkillItem>
                ),
              )}
            </SkillList>
            {/* <ResumeButton onClick={handleResumeDownload}>
              Download Resume
            </ResumeButton> */}
          </SkillCard>

          <SkillCard>
            <Title>
              <Develope width={40} height={40} /> Machine Learning
            </Title>
            <Description>
              A creative machine learning innovator who develops human-centered
              AI solutions, from custom neural network frameworks like NeuraC to
              cutting-edge generative models, applying these technologies to
              create innovative solutions across diverse domains.
            </Description>
            <SectionTitle>Skills</SectionTitle>
            <SkillList>
              {[
                'Deep Learning',
                'NLP',
                'OpenCV',
                'Generative AI',
                'LLMs',
                'RAG',
                'FineTuning LLMs',
                'GANs',
              ].map((skill) => (
                <SkillItem key={skill}>{skill}</SkillItem>
              ))}
            </SkillList>
            <SectionTitle>Tools</SectionTitle>
            <SkillList>
              {[
                'Pytorch',
                'Langchain',
                'Axolotl',
                'VectorDB',
                'Sagemaker',
                'HuggingFace',
              ].map((tool) => (
                <SkillItem key={tool}>{tool}</SkillItem>
              ))}
            </SkillList>
            {/* <ResumeButton onClick={handleResumeDownload}>
              Download Resume
            </ResumeButton> */}
          </SkillCard>
        </Container>
        <BigTitle text="SKILLS" top="80%" right="30%" />
      </Box>
    </ThemeProvider>
  );
};

export default MySkillsPage;
